/**
 * Internal helpers for deterministic avatar color and initials computation.
 *
 * These are INTERNAL to the Notification primitive — NOT exported from the
 * shared/ui barrel. They live in a sibling file (not inside index.tsx) so
 * unit tests can import them directly without importing the full React
 * component tree.
 */

/**
 * Hash-selected avatar palette. Ordered so index 0 is `violet` — the
 * empty-string hash resolves to 0 via `Math.abs(0) % N`, which lets the
 * empty-input test stay pinned to a stable color regardless of the
 * palette's length. Don't reorder without also updating the empty-input
 * regression test in avatarColor.test.ts.
 *
 * `green` is intentionally omitted — it's still a supported Avatar
 * prop for back-compat, but it sits too close to the emerald brand
 * color to carry per-sender identity cleanly. The current 5 colors are
 * chosen for strong visual differentiation in group chats (warm/cool
 * spread, no hue overlaps with brand or negative-red).
 */
export const AVATAR_PALETTE = ["violet", "fuchsia", "amber", "lime", "sky"] as const;
export type AvatarPaletteColor = (typeof AVATAR_PALETTE)[number];

/**
 * Deterministic hash from a sender string to a palette color. Stable across
 * renders for the same sender so the same person always gets the same color.
 */
export function avatarColorForSender(name: string): AvatarPaletteColor {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

/**
 * Extract display initials from a name. Rules:
 *   - Splits on whitespace, ignores empty tokens.
 *   - 0 tokens (empty / whitespace-only string) → "?"
 *   - 1 token → first character (code-point), uppercased.
 *   - 2+ tokens → first char of first token + first char of last token,
 *     both uppercased.
 *
 * Iterates by Unicode code point (via the spread operator) rather than
 * UTF-16 code unit, so emoji and non-BMP characters (e.g. CJK Ext-B) render
 * as whole glyphs instead of surrogate halves.
 */
function firstCodePoint(s: string): string {
  return [...s][0] ?? "";
}

export function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return firstCodePoint(words[0]).toUpperCase();
  return (firstCodePoint(words[0]) + firstCodePoint(words[words.length - 1])).toUpperCase();
}
